// src/pages/time/LeaveSummaryPage.jsx
// Extracted from App.js - Leave Summary admin page
// BRIO Migration: Using code "4a" (table dense)
import React, { useState } from 'react';
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioByCode,
  BrioTable,
  BrioProgress,
  BrioBadge,
  getTablePropsFromCode
} from '../../components/BrioComponents';

// Presentation code for this page
const PRESENTATION_CODE = '4a';

export default function LeaveSummaryPage({
  // Data
  activeUsers,
  selectedLeaveYear,
  leaveAuditLog,
  filters,
  form,
  // State setters
  setSelectedLeaveYear,
  setFilters,
  setForm,
  // Functions
  getYearlyLeaveSummary,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);

  // Local fallback filter state
  const [localFilters, setLocalFilters] = useState({ employmentStatus: 'active' });

  // Safe effective filters and setter
  const hasExternalFilters = typeof setFilters === 'function';
  const effectiveFilters = (filters && typeof filters === 'object') ? filters : localFilters;
  const effectiveSetFilters = hasExternalFilters ? setFilters : setLocalFilters;
  const employmentStatus = effectiveFilters.employmentStatus || 'active';

  // Local fallback sort state (used when form/setForm not provided)
  const [localSort, setLocalSort] = useState({ key: 'employee', direction: 'asc' });
  const hasExternalForm = typeof setForm === 'function' && form && typeof form === 'object';
  const sortConfig = hasExternalForm
    ? (form.leaveSummarySort || { key: 'employee', direction: 'asc' })
    : localSort;
  const updateSort = (sortKey) => {
    const newDirection = sortConfig.key === sortKey && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    const next = { key: sortKey, direction: newDirection };
    if (hasExternalForm) {
      setForm({ ...form, leaveSummarySort: next });
    } else {
      setLocalSort(next);
    }
  };
  const sortIndicator = (sortKey) =>
    sortConfig.key === sortKey ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : '';

  // Filter summaries based on employment status
  const filteredSummaries = getYearlyLeaveSummary(selectedLeaveYear).filter(summary => {
    const u = summary.user;
    if (employmentStatus === 'active') return u?.isActive !== false;
    if (employmentStatus === 'inactive') return u?.isActive === false;
    return true;
  });

  // Sort filtered summaries based on current sort config
  const sortedSummaries = [...filteredSummaries].sort((a, b) => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    switch (sortConfig.key) {
      case 'employee':
        return (a.user?.name || '').toLowerCase().localeCompare((b.user?.name || '').toLowerCase()) * dir;
      case 'role':
        return (a.user?.jobRole || '').toLowerCase().localeCompare((b.user?.jobRole || '').toLowerCase()) * dir;
      case 'sick':
        return ((a.leaveTaken.sick || 0) - (b.leaveTaken.sick || 0)) * dir;
      case 'wfh':
        return ((a.leaveTaken.wfh || 0) - (b.leaveTaken.wfh || 0)) * dir;
      case 'vacation':
        return ((a.leaveTaken.vacation || 0) - (b.leaveTaken.vacation || 0)) * dir;
      case 'emergency':
        return ((a.leaveTaken.emergency || 0) - (b.leaveTaken.emergency || 0)) * dir;
      case 'used':
        return ((a.leaveTaken.total || 0) - (b.leaveTaken.total || 0)) * dir;
      case 'remaining':
        return ((a.remaining || 0) - (b.remaining || 0)) * dir;
      case 'utilization':
        return ((a.utilizationPercent || 0) - (b.utilizationPercent || 0)) * dir;
      default:
        return 0;
    }
  });

  return (
    <div className="brio-flex brio-flex-col brio-analytics-font" style={{ background: 'var(--brio-white)' }}>
      {/* PAGE HEADER BAND: Full-width white background, centered content */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">

              <p className="brio-analytics-muted brio-mt-xs">Working days taken vs entitlement (Mon-Fri, excludes weekends, holidays, and sick leave)</p>
            </div>
            <div className="brio-page-header__right">
              <div className="brio-flex brio-items-center brio-gap-xs">
                <button
                  onClick={() => effectiveSetFilters({
                    ...effectiveFilters,
                    employmentStatus: employmentStatus === 'active' ? 'inactive' : 'active'
                  })}
                  className={`brio-btn ${employmentStatus === 'active' ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                  title="Toggle active / inactive"
                >
                  {employmentStatus === 'active' ? 'Active' : 'Inactive'}
                </button>
                <label className="brio-text-m brio-font-medium brio-text-grey">Year:</label>
                <select
                  value={selectedLeaveYear}
                  onChange={(e) => setSelectedLeaveYear(parseInt(e.target.value))}
                  className="brio-btn brio-btn-secondary"
                >
                  {Array.from({ length: 10 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return <option key={year} value={year}>{year}</option>;
                  })}
                </select>
              </div>
            </div>
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          {/* Leave Summary Table */}
          <BrioAnalyticsSurface padding="m">
            <BrioByCode code={PRESENTATION_CODE}>
              <BrioTable {...tableProps}>
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => updateSort('employee')}>
                      Employee{sortIndicator('employee')}
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => updateSort('role')}>
                      Role{sortIndicator('role')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => updateSort('sick')}>
                      Sick{sortIndicator('sick')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => updateSort('wfh')}>
                      WFH{sortIndicator('wfh')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => updateSort('vacation')}>
                      Vacation{sortIndicator('vacation')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => updateSort('emergency')}>
                      Emergency{sortIndicator('emergency')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => updateSort('used')}>
                      Used / Entitled{sortIndicator('used')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => updateSort('remaining')}>
                      Remaining{sortIndicator('remaining')}
                    </th>
                    <th className="text-center" style={{ minWidth: '180px', cursor: 'pointer' }} onClick={() => updateSort('utilization')}>
                      Utilization{sortIndicator('utilization')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSummaries.map(summary => {
                    const { user, entitlement, leaveTaken, remaining, utilizationPercent, hasExceeded, isNearLimit } = summary;
                    const rowStyle = hasExceeded
                      ? { background: 'var(--brio-red-light)' }
                      : isNearLimit
                        ? { background: 'var(--brio-yellow-light)' }
                        : {};
                    const cellTop = { verticalAlign: 'top' };
                    const sickOver = Math.max(0, leaveTaken.sick - 5);
                    const wfhOver = Math.max(0, (leaveTaken.wfh || 0) - 5);
                    const emergencyHelper = (leaveTaken.emergency || 0) >= 5 ? '(at annual cap)' : `(${Math.max(0, 5 - (leaveTaken.emergency || 0))} of 5 left)`;
                    return (
                      <tr key={user.id} className="brio-row--hoverable" style={rowStyle}>
                        <td className="brio-font-medium" style={cellTop}>{user.name}</td>
                        <td className="brio-text-grey" style={cellTop}>{user.jobRole}</td>
                        <td className="text-center" style={cellTop}>
                          <div>{leaveTaken.sick}</div>
                          <div className="brio-mt-xs brio-text-xs">
                            {sickOver > 0 ? (
                              <BrioBadge variant="danger">+{sickOver} over limit</BrioBadge>
                            ) : ' '}
                          </div>
                        </td>
                        <td className="text-center" style={cellTop}>
                          <div>{leaveTaken.wfh || 0}</div>
                          <div className="brio-mt-xs brio-text-xs">
                            {wfhOver > 0 ? (
                              <BrioBadge variant="danger">+{wfhOver} over limit</BrioBadge>
                            ) : ' '}
                          </div>
                        </td>
                        <td className="text-center" style={cellTop}>
                          <div>{leaveTaken.vacation}</div>
                          <div className="brio-mt-xs brio-text-xs">&nbsp;</div>
                        </td>
                        <td className="text-center" style={cellTop}>
                          <div>{leaveTaken.emergency || 0}</div>
                          <div className="brio-mt-xs brio-text-xs brio-text-grey">{emergencyHelper}</div>
                        </td>
                        <td className="text-center brio-font-medium" style={{ ...cellTop, color: 'var(--brio-gold)' }}>
                          <div>{leaveTaken.total} / {entitlement.finalTotalLeave} days</div>
                          <div className="brio-mt-xs brio-text-xs">&nbsp;</div>
                        </td>
                        <td className="text-center brio-font-bold" style={{ ...cellTop, color: 'var(--brio-gold)' }}>
                          <div>{remaining.toFixed(1)} days</div>
                          <div className="brio-mt-xs brio-text-xs">&nbsp;</div>
                        </td>
                        <td className="text-center" style={{ ...cellTop, minWidth: '180px' }}>
                          <BrioProgress
                            value={utilizationPercent}
                            variant={utilizationPercent > 100 ? 'danger' : utilizationPercent > 80 ? 'warning' : 'normal'}
                            tall
                          />
                          <div className="brio-mt-xs brio-text-xs brio-text-grey">
                            {utilizationPercent.toFixed(0)}% utilized
                          </div>
                          {hasExceeded && (
                            <BrioBadge variant="danger" className="brio-mt-xs">Exceeded</BrioBadge>
                          )}
                          {isNearLimit && !hasExceeded && (
                            <BrioBadge variant="warning" className="brio-mt-xs">Near limit</BrioBadge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </BrioTable>
            </BrioByCode>
            {sortedSummaries.length === 0 && (
              <div className="brio-text-center brio-analytics-muted" style={{ padding: '32px 0' }}>
                No leave summary entries
              </div>
            )}
          </BrioAnalyticsSurface>

          {/* Audit Log */}
          <BrioAnalyticsSurface padding="m">
            <h3 className="brio-analytics-section-title brio-mb-s">Leave Management Audit Log</h3>
            <BrioByCode code={PRESENTATION_CODE}>
              <BrioTable {...tableProps}>
                <thead>
                  <tr>
                    <th>action</th>
                    <th>details</th>
                    <th className="text-right">timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveAuditLog.slice(0, 50).map(log => {
                    const performer = activeUsers.find(u => u.id === log.performedBy);
                    return (
                      <tr key={log.id} className="brio-row--hoverable">
                        <td>
                          <span className="brio-font-medium" style={{ textTransform: 'capitalize' }}>{log.action.replace(/_/g, ' ')}</span>
                        </td>
                        <td>
                          <span className="brio-text-grey">
                            {log.userName && `Employee: ${log.userName} | `}
                            {log.roleLevel && `Role: ${log.roleLevel} | `}
                            {log.field && `Field: ${log.field} | `}
                            {log.oldValue !== undefined && `Old: ${log.oldValue} → `}
                            {log.newValue !== undefined && `New: ${log.newValue} | `}
                            {log.details && log.details}
                            {performer && ` | By: ${performer.name}`}
                          </span>
                        </td>
                        <td className="text-right">
                          <span className="brio-text-grey">{new Date(log.timestamp).toLocaleString()}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </BrioTable>
            </BrioByCode>
            {leaveAuditLog.length === 0 && (
              <div className="brio-text-center brio-analytics-muted" style={{ padding: '32px 0' }}>
                No audit entries yet
              </div>
            )}
          </BrioAnalyticsSurface>
        </div>
      </BrioFrame>
    </div>
  );
}
